import React, { useState, useEffect } from 'react';
import { getSharedPlan, getSharePermissions } from '../utils/sharing';
import { trackEvent } from '../utils/telemetry';

export default function PublicGuestShowcase({ shareToken, onStartOwnPlan }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function loadShare() {
      try {
        setLoading(true);
        setError(null);
        const [plan, perms] = await Promise.all([
          getSharedPlan(shareToken),
          getSharePermissions(shareToken).catch(() => ({ can_view: true }))
        ]);
        if (isMounted) {
          setPlanData(plan);
          setPermissions(perms);
          trackEvent('guest_showcase_viewed', {
            share_token: shareToken,
            occasion: plan?.event_data?.event_type || plan?.event_data?.occasion || 'Celebration',
            theme: plan?.selected_theme?.name || 'Party Theme'
          });
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error loading shared plan:', err);
          setError('This party plan link could not be found or has expired.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    if (shareToken) {
      loadShare();
    }
    return () => {
      isMounted = false;
    };
  }, [shareToken]);

  const handleCtaClick = (source = 'bottom_banner') => {
    trackEvent('guest_showcase_cta_clicked', { source, share_token: shareToken });
    if (onStartOwnPlan) {
      onStartOwnPlan();
    } else {
      window.location.href = '/';
    }
  };

  const handleShareClick = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-12 h-12 rounded-full border-3 border-stone-200 border-t-stone-900 animate-spin mb-4" />
        <h2 className="text-base font-bold text-stone-800">Unwrapping party showcase...</h2>
        <p className="text-xs text-stone-500 mt-1">Loading theme, moodboard, and celebration details</p>
      </div>
    );
  }

  if (error || !planData) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-600 border border-amber-200 flex items-center justify-center text-2xl mb-4">
          🎈
        </div>
        <h2 className="text-xl font-black text-stone-900 mb-2">Party Link Not Available</h2>
        <p className="text-sm text-stone-600 max-w-md mb-6">{error || 'This party showcase is no longer available.'}</p>
        <button
          type="button"
          onClick={() => handleCtaClick('error_page')}
          className="px-6 py-3 bg-stone-950 text-white rounded-full text-xs font-bold shadow-md hover:bg-stone-800 transition"
        >
          Plan Your Own Celebration Free &rarr;
        </button>
      </div>
    );
  }

  const event = planData.event_data || {};
  const selectedTheme = planData.selected_theme || (planData.themes && planData.themes[0]) || {};
  const currentPlan = (planData.theme_plans && (planData.theme_plans[selectedTheme?.name] || planData.theme_plans[selectedTheme?.id])) || {};

  const occasion = event.event_type || event.occasion || 'Celebration';
  const themeName = selectedTheme.name || 'Bespoke Theme';
  const themeDescription = selectedTheme.description || selectedTheme.concept || 'A tailored aesthetic experience crafted for unforgettable memories.';
  const dateStr = event.date || 'Upcoming Weekend';
  const venueStr = event.venue_name || (event.zip_code ? `Private Venue (Near ${event.zip_code})` : 'Private Location');
  const dressCode = selectedTheme.dress_code?.recommendation || selectedTheme.dress_code || 'Smart Casual Chic';

  // Palette colors
  const rawPalette = selectedTheme.palette || ['#09090B', '#E11D48', '#FB923C', '#FBBF24', '#FAF9F6'];
  const palette = rawPalette.map(c => typeof c === 'string' ? { hex: c, name: '' } : c);

  // Google Calendar Link generator
  const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`${occasion}: ${themeName}`)}&details=${encodeURIComponent(`You're invited to celebrate ${occasion}!\nTheme: ${themeName}\nAttire: ${dressCode}\n\nView details: ${window.location.href}`)}&location=${encodeURIComponent(venueStr)}`;

  // Menu items preview
  const menuItems = currentPlan.menu?.items || currentPlan.food_items || [];
  const timelineItems = currentPlan.timeline?.schedule || currentPlan.schedule || [];

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-stone-900 w-full max-w-full overflow-x-clip font-sans selection:bg-stone-900 selection:text-white">
      {/* Top Floating Guest Bar */}
      <header className="sticky top-0 z-40 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-stone-200/80 px-4 sm:px-8 py-3.5">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <a href="/" className="flex items-center gap-2 text-decoration-none">
            <img src="/new_icon+text_color.png" alt="CraftMyParty" className="h-9 sm:h-11 w-auto object-contain" />
          </a>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleShareClick}
              className="px-3.5 py-1.5 rounded-full border border-stone-300 text-xs font-bold text-stone-700 hover:bg-stone-100 transition flex items-center gap-1.5"
            >
              <span>{copiedLink ? '✓ Copied' : '🔗 Share'}</span>
            </button>
            <button
              type="button"
              onClick={() => handleCtaClick('top_nav')}
              className="px-4 sm:px-5 py-1.5 rounded-full bg-stone-950 text-white text-xs font-bold shadow-sm hover:bg-stone-800 transition"
            >
              Start Free
            </button>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-24">
        {/* Celebration Header */}
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-[11px] sm:text-xs font-bold tracking-wider uppercase mb-4 bg-stone-100 text-stone-800 border border-stone-200/80 shadow-2xs">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Official Guest Guide</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black text-stone-950 tracking-tight leading-tight mb-3">
            {occasion}
          </h1>
          <p className="text-lg sm:text-2xl font-serif italic text-stone-700 mb-6">
            Theme: {themeName}
          </p>

          {/* Quick Info Badges */}
          <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3 text-xs font-semibold text-stone-700">
            <div className="flex items-center gap-1.5 bg-white px-3.5 py-2 rounded-xl border border-stone-200/80 shadow-2xs">
              <span>📅</span>
              <span>{dateStr}</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white px-3.5 py-2 rounded-xl border border-stone-200/80 shadow-2xs">
              <span>📍</span>
              <span>{venueStr}</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white px-3.5 py-2 rounded-xl border border-stone-200/80 shadow-2xs">
              <span>👗</span>
              <span>{dressCode}</span>
            </div>
          </div>

          {/* Action Row */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a
              href={gcalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-stone-900 text-white text-xs font-bold shadow-md hover:bg-stone-800 transition active:scale-95"
            >
              <span>📅</span>
              <span>Add to Google Calendar</span>
            </a>
          </div>
        </div>

        {/* Aesthetic Visualizer Card */}
        <section className="bg-white rounded-3xl border border-stone-200 shadow-xl overflow-hidden mb-10">
          {/* Header Bar */}
          <div className="p-5 sm:p-7 border-b border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-stone-50/50">
            <div>
              <span className="text-[10px] uppercase font-bold tracking-widest text-stone-400 block mb-0.5">Atmosphere & Mood</span>
              <h2 className="text-xl font-extrabold text-stone-900 tracking-tight">{themeName}</h2>
            </div>
            {/* Color Palette Swatches */}
            <div className="flex items-center gap-1.5">
              {palette.slice(0, 5).map((color, idx) => (
                <div
                  key={idx}
                  className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-white shadow-xs"
                  style={{ backgroundColor: color.hex }}
                  title={color.name || color.hex}
                />
              ))}
            </div>
          </div>

          {/* Body Content */}
          <div className="p-6 sm:p-8">
            <p className="text-sm sm:text-base text-stone-600 leading-relaxed max-w-2xl mb-8">
              {themeDescription}
            </p>

            {/* 3-Column Guest Guide */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Attire Card */}
              <div className="bg-stone-50 rounded-2xl p-4 sm:p-5 border border-stone-200/70 flex flex-col justify-between">
                <div>
                  <div className="w-8 h-8 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center text-base mb-3">
                    👗
                  </div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Attire Recommendation</h3>
                  <p className="text-sm font-bold text-stone-900">{dressCode}</p>
                </div>
                <span className="text-[11px] text-stone-400 mt-4">Dress to match the celebration palette</span>
              </div>

              {/* Venue / Location Card */}
              <div className="bg-stone-50 rounded-2xl p-4 sm:p-5 border border-stone-200/70 flex flex-col justify-between">
                <div>
                  <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center text-base mb-3">
                    📍
                  </div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Venue & Timing</h3>
                  <p className="text-sm font-bold text-stone-900">{venueStr}</p>
                </div>
                <span className="text-[11px] text-stone-400 mt-4">{dateStr}</span>
              </div>

              {/* Guest Count / Vibe */}
              <div className="bg-stone-50 rounded-2xl p-4 sm:p-5 border border-stone-200/70 flex flex-col justify-between">
                <div>
                  <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center text-base mb-3">
                    ✨
                  </div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">Guest Atmosphere</h3>
                  <p className="text-sm font-bold text-stone-900">{event.guest_count ? `Intimate ~${event.guest_count} Guests` : 'Festive & Curated'}</p>
                </div>
                <span className="text-[11px] text-stone-400 mt-4">Curated with CraftMyParty Studio</span>
              </div>
            </div>
          </div>
        </section>

        {/* Menu & Timeline Preview Highlights (if available) */}
        {(menuItems.length > 0 || timelineItems.length > 0) && (
          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            {/* Menu Highlights */}
            {menuItems.length > 0 && (
              <section className="bg-white rounded-3xl p-6 sm:p-7 border border-stone-200 shadow-md">
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="text-xl">🍸</span>
                  <h3 className="text-base font-extrabold text-stone-900">Food & Sips Preview</h3>
                </div>
                <ul className="space-y-3">
                  {menuItems.slice(0, 4).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-stone-700">
                      <span className="text-stone-400 font-bold">•</span>
                      <span>{typeof item === 'string' ? item : (item.name || item.dish || 'Curated bite')}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Schedule Highlights */}
            {timelineItems.length > 0 && (
              <section className="bg-white rounded-3xl p-6 sm:p-7 border border-stone-200 shadow-md">
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="text-xl">⏰</span>
                  <h3 className="text-base font-extrabold text-stone-900">Schedule Highlights</h3>
                </div>
                <ul className="space-y-3">
                  {timelineItems.slice(0, 4).map((t, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-stone-700">
                      <span className="text-stone-400 font-bold">⏱</span>
                      <span>{typeof t === 'string' ? t : `${t.time || ''} ${t.event || t.title || ''}`}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}

        {/* Viral Acquisition Footer Banner */}
        <section className="relative rounded-3xl bg-stone-950 text-white p-8 sm:p-12 text-center overflow-hidden shadow-2xl">
          <div
            className="absolute inset-0 pointer-events-none opacity-20"
            style={{ background: 'radial-gradient(circle at center, #A855F7, transparent 70%)' }}
          />
          <div className="relative z-10 max-w-xl mx-auto">
            <span className="text-[11px] uppercase tracking-widest text-emerald-400 font-bold block mb-3">
              CraftMyParty · AI Event Director
            </span>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-3">
              Planning a celebration of your own?
            </h2>
            <p className="text-stone-400 text-xs sm:text-sm leading-relaxed mb-6">
              Skip the messy Pinterest boards and endless spreadsheets. Get bespoke themes, interactive room moodboards, smart timelines, and 1-click shopping lists in 2 minutes.
            </p>
            <button
              type="button"
              onClick={() => handleCtaClick('bottom_viral_banner')}
              className="px-7 py-3.5 rounded-full bg-white hover:bg-stone-100 text-stone-950 font-bold text-xs sm:text-sm shadow-xl transition active:scale-95 flex items-center justify-center gap-2 mx-auto"
            >
              <span>Start Planning Your Event Free</span>
              <span>&rarr;</span>
            </button>
            <p className="text-[11px] text-stone-500 mt-4">
              Free to start • No account or credit card required
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
